// GAP-15a — Bettor Archetype mapping (OddsJam Wave 1).
//
// Founder ruling: ask the OddsJam spec's 3-archetype survey but
// RESOLVE to STRIKZA's existing 4 archetypes (single source of
// truth — the values already on user_betting_profile.archetype).
// This module is the decision-as-code; the survey UI (GAP-15b)
// consumes it. Pure, no I/O.
//
// Mapping rationale (documented so it's adjustable, not magic):
//   Recreational      → intelligence_learner
//       early-journey, still learning process/discipline.
//   Systematic        → disciplined_compounder
//       process-driven, compounding — the closest 1:1.
//   Syndicate-Aspiring → value_seeker
//       pure edge/volume focus at scale.
//   (jackpot_hunter has no doc equivalent — never produced by this
//   survey; it is reached via behavioural signals elsewhere.)

// STRIKZA's canonical 4 (must match user_betting_profile.archetype).
export type StrikzaArchetype =
  | "value_seeker"
  | "jackpot_hunter"
  | "disciplined_compounder"
  | "intelligence_learner";

// The OddsJam spec's 3 survey outcomes.
export type SurveyArchetype =
  | "recreational"
  | "systematic"
  | "syndicate_aspiring";

export const SURVEY_TO_STRIKZA: Record<
  SurveyArchetype,
  StrikzaArchetype
> = {
  recreational: "intelligence_learner",
  systematic: "disciplined_compounder",
  syndicate_aspiring: "value_seeker",
};

export interface ArchetypeQuestion {
  readonly id: string;
  readonly prompt: string;
  /** Each answer carries weight toward one survey archetype. */
  readonly options: ReadonlyArray<{
    readonly label: string;
    readonly weight: SurveyArchetype;
  }>;
}

// 5-question survey (spec GAP-15 step 1). Deterministic scoring:
// the survey archetype with the most selected-option weight wins;
// ties resolve by SURVEY_ORDER (most conservative first).
export const SURVEY_ORDER: readonly SurveyArchetype[] = [
  "recreational",
  "systematic",
  "syndicate_aspiring",
];

export const ARCHETYPE_SURVEY: readonly ArchetypeQuestion[] = [
  {
    id: "stake_discipline",
    prompt: "How do you decide your stake?",
    options: [
      { label: "By feel / what I can afford that day", weight: "recreational" },
      { label: "A fixed unit or simple rule", weight: "systematic" },
      { label: "Sized to edge (Kelly-style)", weight: "syndicate_aspiring" },
    ],
  },
  {
    id: "record_keeping",
    prompt: "Do you track your bets?",
    options: [
      { label: "Not really", weight: "recreational" },
      { label: "A spreadsheet / notes", weight: "systematic" },
      { label: "CLV and closing-line discipline", weight: "syndicate_aspiring" },
    ],
  },
  {
    id: "volume",
    prompt: "How many bets in a typical week?",
    options: [
      { label: "A few, around big games", weight: "recreational" },
      { label: "A steady routine", weight: "systematic" },
      { label: "High volume, many surfaces", weight: "syndicate_aspiring" },
    ],
  },
  {
    id: "goal",
    prompt: "What are you here for?",
    options: [
      { label: "Learn to bet smarter", weight: "recreational" },
      { label: "Compound a bankroll steadily", weight: "systematic" },
      { label: "Operate at edge, at scale", weight: "syndicate_aspiring" },
    ],
  },
  {
    id: "books",
    prompt: "How many bookmaker accounts do you use?",
    options: [
      { label: "One", weight: "recreational" },
      { label: "Two or three", weight: "systematic" },
      { label: "Several — I shop every price", weight: "syndicate_aspiring" },
    ],
  },
];

/** Deterministic survey scoring → STRIKZA archetype. `answers` is
 *  one chosen option index per question (out of range / missing ⇒
 *  ignored, never invented). Empty ⇒ recreational (safest default). */
export function resolveArchetype(
  answers: ReadonlyArray<number>,
): { survey: SurveyArchetype; strikza: StrikzaArchetype } {
  const tally: Record<SurveyArchetype, number> = {
    recreational: 0,
    systematic: 0,
    syndicate_aspiring: 0,
  };
  ARCHETYPE_SURVEY.forEach((q, i) => {
    const pick = answers[i];
    if (typeof pick !== "number") return;
    const opt = q.options[pick];
    if (opt) tally[opt.weight] += 1;
  });
  let best: SurveyArchetype = "recreational";
  let bestN = -1;
  for (const a of SURVEY_ORDER) {
    if (tally[a] > bestN) {
      best = a;
      bestN = tally[a];
    }
  }
  return { survey: best, strikza: SURVEY_TO_STRIKZA[best] };
}
